import { BulbOutlined, FileExclamationFilled } from "@ant-design/icons";
import { faCircleExclamation, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Select, Tag, Tooltip } from "antd";
import convertFormulaBuilderToString from "common/convertFormulaBuilderToString";
import { Parser } from "hot-formula-parser";
import { isEmpty } from "lodash";
import React from "react";
import DataMapperBuilderInput from "./DataMapperBuilderInput";

const { Option } = Select;

type DataMapperProps = {
    destinationFields: IDataMappingField[];
    sourceFields: string[];
    sourceData: IFormulaParserDataInput[];
    row?: number;
    onMappingChanged: (mappedField: IDataMappingField) => void;
    aiMappingSuggestions?: Array<{
        sourceColumn: string;
        targetAttribute: string;
        confidence: number;
        reasoning: string;
        sourceColumnExists: boolean;
        targetAttributeExists: boolean;
    }>;
};

const DataMapper = ({
    destinationFields,
    sourceFields,
    sourceData,
    row = 0, // Zero-based array
    onMappingChanged,
    aiMappingSuggestions = [],
}: DataMapperProps) => {
    const onMappingTypeChange = React.useCallback(
        (field: IDataMappingField, value: IDataMappingType) => {
            if (field.mappingType === value) {
                // Nothing has changed
                return;
            }

            const changedField: IDataMappingField = {
                ...field,
                mappingType: value,
                value: "",
                sourceColumns: [],
                variables: [],
                builder: undefined,
            };

            onMappingChanged(changedField);
        },
        [onMappingChanged],
    );

    const onDataColumnChange = React.useCallback(
        (field: IDataMappingField, value: string) => {
            if (field.value === value) {
                // Nothing has changed
                return;
            }

            const changedField: IDataMappingField = {
                ...field,
                value: value ?? "",
                sourceColumns: value ? [value] : [],
            };

            onMappingChanged(changedField);
        },
        [onMappingChanged],
    );

    // Create dropdown options with AI suggestions at the top for each field
    const getSourceFieldOptionsForField = React.useCallback(
        (fieldName: string) => {
            const options: { label: string; value: string; confidence?: number; isAiSuggestion?: boolean }[] = [];
            const aiSuggestion = aiMappingSuggestions.find((s) => s.targetAttribute === fieldName);

            // Add AI suggestion first if it exists
            if (aiSuggestion && aiSuggestion.sourceColumnExists) {
                options.push({
                    value: aiSuggestion.sourceColumn,
                    label: aiSuggestion.sourceColumn,
                    confidence: aiSuggestion.confidence,
                    isAiSuggestion: true,
                });
            }

            // Add all other source fields
            const sortedFields = [...sourceFields].sort();
            sortedFields
                .filter((a, i) => {
                    // Remove duplicates and exclude the AI suggestion field
                    if (i === 0) {
                        return aiSuggestion?.sourceColumn !== a;
                    }
                    return a !== sortedFields[i - 1] && aiSuggestion?.sourceColumn !== a;
                })
                .forEach((f) =>
                    options.push({
                        value: f,
                        label: f,
                        isAiSuggestion: false,
                    }),
                );

            return options;
        },
        [sourceFields, aiMappingSuggestions],
    );

    const handleComputeSample = React.useCallback(
        (field: IDataMappingField): string | null => {
            if (!sourceData) {
                return "";
            }

            if (field.mappingType === "FORMULA" && field.builder) {
                const derivedFormula = convertFormulaBuilderToString(field.builder);
                const parser = new Parser();
                derivedFormula.variables.forEach((variable) => {
                    const rowData = sourceData[row] as Record<string, any>;
                    const hasKey = rowData && Object.prototype.hasOwnProperty.call(rowData, variable.value);
                    const valueForVar = hasKey ? (rowData[variable.value] ?? "") : "Unknown Variable";
                    parser.setVariable(variable.name, valueForVar);
                });
                const computed = parser.parse(derivedFormula.formula);

                if (!computed.error) {
                    return `${computed.result}`;
                }
            } else if (field.mappingType === "DATA_COLUMN") {
                // In case the header is empty value
                if (!isEmpty(field.value) && sourceData[row]) {
                    return sourceData[row][field.value];
                }
            }

            return null;
        },
        [row, sourceData],
    );

    const duplicateSourceFields = React.useMemo(() => {
        const counts: Record<string, number> = {};
        destinationFields.forEach((f) => {
            if (f.mappingType === "DATA_COLUMN" && f.value) {
                counts[f.value] = (counts[f.value] ?? 0) + 1;
            }
        });
        return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
    }, [destinationFields]);

    // Helper function to get AI suggestion for a field
    const getAiSuggestionForField = React.useCallback(
        (fieldName: string) => {
            return aiMappingSuggestions.find((suggestion) => suggestion.targetAttribute === fieldName);
        },
        [aiMappingSuggestions],
    );

    // Helper function to apply AI suggestion
    const applyAiSuggestion = React.useCallback(
        (field: IDataMappingField, suggestion: any) => {
            const changedField: IDataMappingField = {
                ...field,
                mappingType: "DATA_COLUMN",
                value: suggestion.sourceColumn,
                sourceColumns: [suggestion.sourceColumn],
            };
            onMappingChanged(changedField);
        },
        [onMappingChanged],
    );

    return (
        <div className="flex size-full flex-1 flex-col">
            <div className="grid grid-cols-[max-content_max-content_200px_1fr_300px] text-sm">
                <div className="border-b border-r border-gray-100 border-gray-200/50 bg-gray-50 p-2 font-bold">Name</div>
                <div className="border-b border-r border-gray-100 border-gray-200/50 bg-gray-50 p-2 font-bold">Data Type</div>
                <div className="border-b border-r border-gray-100 border-gray-200/50 bg-gray-50 p-2 font-bold">Mapping Type</div>
                <div className="border-b border-r border-gray-100 border-gray-200/50 bg-gray-50 p-2 font-bold">Mapped Value</div>
                <div className="border-b border-gray-100 bg-gray-50 p-2 font-bold">Sample Value</div>
                {destinationFields.map((field, index) => (
                    <React.Fragment key={`${field.name}-${index}`}>
                        <div className="flex flex-row items-center justify-between gap-2 border-b border-gray-100 p-2">
                            <Tooltip placement="topLeft" title={field.name}>
                                {field.name}
                            </Tooltip>
                            <div className="flex items-center gap-1" data-testid={`datamapper-icon-${field.name}`}>
                                {!field.value && (
                                    <Tooltip
                                        placement="top"
                                        title={field.isRequired || field.isPrimaryKey ? `'${field.name}' is a required field and is not mapped` : `'${field.name}' is not mapped`}
                                    >
                                        <FontAwesomeIcon icon={faCircleExclamation} className={field.isRequired || field.isPrimaryKey ? "text-red-500" : "text-orange-400"} />
                                    </Tooltip>
                                )}
                                {field.value && duplicateSourceFields.has(field.value) && (
                                    <Tooltip placement="top" title={`'${field.value}' is mapped to multiple destination fields`}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500" />
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-row items-center border-b border-gray-100 p-2">
                            <Tooltip placement="topLeft" title={field.dataType}>
                                {field.dataType}
                            </Tooltip>
                        </div>

                        <div className="flex flex-row items-center border-b border-gray-100 p-2">
                            <Select<IDataMappingType>
                                style={{ width: "100%" }}
                                defaultValue={field.mappingType ?? "DATA_COLUMN"}
                                onChange={(value) => onMappingTypeChange(field, value)}
                                value={field.mappingType}
                            >
                                <Option value="DATA_COLUMN">Data column</Option>
                                <Option value="FORMULA">Formula</Option>
                            </Select>
                        </div>

                        {field.mappingType === "DATA_COLUMN" ? (
                            <div className="flex flex-col border-b border-gray-100 p-2" data-testid={`datamapper-value-${field.name}`}>
                                {/* AI Suggestion Display */}
                                {(() => {
                                    const aiSuggestion = getAiSuggestionForField(field.name);
                                    return aiSuggestion && !field.value ? (
                                        <div className="mb-2 rounded-md border border-blue-200 bg-blue-50 p-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <BulbOutlined className="text-blue-600" />
                                                    <span className="text-sm font-medium text-blue-800">AI Suggestion:</span>
                                                    <Tag color="blue" className="text-xs">
                                                        {aiSuggestion.sourceColumn}
                                                    </Tag>
                                                    <Tag color={aiSuggestion.confidence > 0.8 ? "green" : aiSuggestion.confidence > 0.6 ? "orange" : "red"} className="text-xs">
                                                        {Math.round(aiSuggestion.confidence)}% confidence
                                                    </Tag>
                                                </div>
                                                <Button size="small" type="primary" onClick={() => applyAiSuggestion(field, aiSuggestion)}>
                                                    Apply
                                                </Button>
                                            </div>
                                            <Tooltip title={aiSuggestion.reasoning} placement="top">
                                                <p className="mt-1 cursor-help text-xs text-blue-700">{aiSuggestion.reasoning}</p>
                                            </Tooltip>
                                        </div>
                                    ) : null;
                                })()}

                                {/* Manual Selection */}
                                <Select
                                    style={{ width: "100%" }}
                                    showSearch
                                    allowClear={true}
                                    placeholder="Search to Select"
                                    options={getSourceFieldOptionsForField(field.name)}
                                    optionFilterProp="label"
                                    onChange={(value) => onDataColumnChange(field, value)}
                                    value={field.value}
                                    status={!field.value ? (field.isRequired || field.isPrimaryKey ? "error" : "warning") : undefined}
                                    labelRender={(label) => {
                                        // Show confidence badge for selected AI suggestion
                                        const aiSuggestion = getAiSuggestionForField(field.name);
                                        const isAiSuggested = aiSuggestion && field.value === aiSuggestion.sourceColumn;

                                        if (isAiSuggested && aiSuggestion) {
                                            return (
                                                <div className="flex items-center gap-2">
                                                    <BulbOutlined className="text-xs text-blue-600" />
                                                    <span>{label.label}</span>
                                                    <Tag color={aiSuggestion.confidence > 0.8 ? "green" : aiSuggestion.confidence > 0.6 ? "orange" : "red"} className="text-xs">
                                                        {Math.round(aiSuggestion.confidence)}%
                                                    </Tag>
                                                </div>
                                            );
                                        }
                                        return label.label;
                                    }}
                                    optionRender={(option) => {
                                        const isAiSuggestion = option.data?.isAiSuggestion;
                                        const confidence = option.data?.confidence;
                                        return (
                                            <div className="flex w-full items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    {isAiSuggestion && <BulbOutlined className="text-blue-600" />}
                                                    <span className={isAiSuggestion ? "font-medium text-blue-700" : ""}>{option.label}</span>
                                                </div>
                                                {isAiSuggestion && confidence && (
                                                    <Tag color={confidence > 0.8 ? "green" : confidence > 0.6 ? "orange" : "red"} className="text-xs">
                                                        {Math.round(confidence)}%
                                                    </Tag>
                                                )}
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        ) : (
                            <DataMapperBuilderInput
                                field={field}
                                sourceColumns={[...sourceFields]}
                                onMappingChanged={onMappingChanged}
                                className="flex flex-row items-center border-b border-gray-100 p-2"
                            />
                        )}
                        <div className="flex flex-row items-center border-b border-gray-100 p-2">{handleComputeSample(field)}</div>
                    </React.Fragment>
                ))}
            </div>
            {destinationFields.length === 0 && (
                <div className="flex w-full flex-1 flex-col p-2">
                    <div className="flex w-full flex-1 flex-col items-center justify-center overflow-auto border border-dashed border-slate-300">
                        <FileExclamationFilled className="mb-3 text-3xl text-slate-500" />
                        <p className="text-sm">No data imported</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataMapper;
